/**
 * Barre de séance (design system, BarreDeSeance, FilDAriane, Progression) : quitter et fil
 * d'Ariane à gauche, progression de la phase au centre, actions de la carte à droite.
 */
import { Icone } from '../composants/Icone';
import { raccourciAria, Touche } from '../composants/Touche';

export function FilDAriane({
  chemin,
  maxElements = 4,
}: {
  chemin: string[];
  maxElements?: number;
}) {
  let elements: { nom: string; titre: string; ellipse?: boolean }[] = chemin.map((nom) => ({
    nom,
    titre: nom,
  }));
  if (chemin.length > maxElements) {
    const replies = chemin.slice(2, -1);
    elements = [
      ...elements.slice(0, 2),
      { nom: '…', titre: replies.join(' › '), ellipse: true },
      elements.at(-1)!,
    ];
  }
  return (
    <nav aria-label="Emplacement de la carte" className="fc-fil-d-ariane">
      <ol className="fc-fil-d-ariane__liste">
        {elements.map((e, i) => (
          <li
            key={i}
            className={
              e.ellipse
                ? 'fc-fil-d-ariane__element fc-fil-d-ariane__element--ellipse'
                : 'fc-fil-d-ariane__element'
            }
            title={e.titre}
            aria-current={i === elements.length - 1 ? 'location' : undefined}
          >
            <span className="fc-fil-d-ariane__nom">{e.nom}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Progression({
  phase,
  faites,
  total,
}: {
  phase: number;
  faites: number;
  total: number;
}) {
  const fini = total > 0 && faites >= total;
  const rang = Math.min(total, faites + 1);
  const part = total === 0 ? 0 : (faites / total) * 100;
  return (
    <div
      className="fc-progression"
      role="progressbar"
      aria-label="Progression de la séance"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={Math.min(faites, total)}
      aria-valuetext={
        fini
          ? `Phase ${phase} terminée, ${total} cartes sur ${total}`
          : `Phase ${phase}, carte ${rang} sur ${total}`
      }
      style={{ ['--part' as string]: part.toFixed(1) }}
    >
      <svg aria-hidden="true" className="fc-progression__camembert" viewBox="0 0 14 14">
        <circle className="fc-progression__contour" cx="7" cy="7" r="6.25" />
        <circle className="fc-progression__secteur" cx="7" cy="7" r="2.25" />
      </svg>
      <span className="fc-progression__rang">
        {fini ? `${total} / ${total}` : `${rang} / ${total}`}
      </span>
      <span className="fc-progression__phase">Phase {phase}</span>
    </div>
  );
}

export interface BarreDeSeanceProps {
  face: 'recto' | 'verso';
  /** Masque les actions de la carte (modification, bilan, alerte). */
  actionsCarte: boolean;
  filDAriane: string[] | null;
  progression: { phase: number; faites: number; total: number } | null;
  annulationPossible: boolean;
  raccourciModifier: string;
  raccourciAnnuler: string | null;
  desactivee?: boolean;
  onQuitter: () => void;
  onModifier: () => void;
  onSuspendre: () => void;
  onAnnuler: () => void;
}

export function BarreDeSeance({
  face,
  actionsCarte,
  filDAriane,
  progression,
  annulationPossible,
  raccourciModifier,
  raccourciAnnuler,
  desactivee = false,
  onQuitter,
  onModifier,
  onSuspendre,
  onAnnuler,
}: BarreDeSeanceProps) {
  const visibles = actionsCarte && face === 'verso';
  return (
    <header className="fc-barre-de-seance" data-face={visibles ? 'verso' : 'recto'}>
      <div className="fc-barre-de-seance__gauche">
        <button
          type="button"
          className="fc-bouton fc-bouton--discret fc-bouton--compact fc-barre-de-seance__action"
          aria-label="Quitter la séance"
          title="Quitter la séance"
          onClick={onQuitter}
        >
          <Icone nom="chevron-left" />
          <span className="fc-barre-de-seance__libelle">Quitter</span>
        </button>
        {filDAriane && filDAriane.length > 0 && <FilDAriane chemin={filDAriane} />}
      </div>
      <div className="fc-barre-de-seance__centre">
        {progression && <Progression {...progression} />}
      </div>
      <div className="fc-barre-de-seance__droite fc-groupe-verre">
        <div
          className="fc-barre-de-seance__actions-carte"
          aria-hidden={visibles ? undefined : true}
        >
          <button
            type="button"
            className="fc-bouton fc-bouton--discret fc-bouton--compact fc-barre-de-seance__action"
            aria-keyshortcuts={raccourciAria(raccourciModifier)}
            aria-label="Modifier la carte"
            title={`Modifier la carte (${raccourciModifier})`}
            tabIndex={visibles ? undefined : -1}
            disabled={desactivee}
            onClick={onModifier}
          >
            <Icone nom="pencil" />
            <span className="fc-barre-de-seance__libelle">Modifier</span>
            <Touche>{raccourciModifier}</Touche>
          </button>
          <button
            type="button"
            className="fc-bouton fc-bouton--discret fc-bouton--compact fc-barre-de-seance__action"
            aria-label="Suspendre la carte"
            title="Retirer la carte des séances jusqu’à sa réactivation"
            tabIndex={visibles ? undefined : -1}
            disabled={desactivee}
            onClick={onSuspendre}
          >
            <Icone nom="eye-off" />
            <span className="fc-barre-de-seance__libelle">Suspendre</span>
          </button>
          <span aria-hidden="true" className="fc-barre-de-seance__separateur" />
        </div>
        <button
          type="button"
          className="fc-bouton fc-bouton--discret fc-bouton--compact fc-bouton--icone"
          aria-label="Annuler la dernière réponse"
          aria-keyshortcuts={raccourciAnnuler ? raccourciAria(raccourciAnnuler) : undefined}
          title={
            raccourciAnnuler
              ? `Annuler la dernière réponse (${raccourciAnnuler})`
              : 'Annuler la dernière réponse'
          }
          aria-disabled={!annulationPossible || desactivee || undefined}
          onClick={() => annulationPossible && !desactivee && onAnnuler()}
        >
          <Icone nom="undo-2" />
        </button>
      </div>
    </header>
  );
}
